import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Box, Chip, IconButton, LinearProgress, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from 'components/shared/CustomPagination/CustomPagination';
import CustomLoader from 'components/shared/CustomLoader/CustomLoader';
import { SortPopover } from 'components/shared/SortPopOver';
import formatCurrency from 'helper/short-currency';
import { ClientDataType, PaginationProps } from 'model/client.model';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import zeplinColor from 'theme/Color';
import {
  getClientHealthScore,
  getClientStatusLabel,
  getHealthBarColor,
  getStatusChipColor,
} from './clientHealth';

type ColumnId =
  | 'Name'
  | 'category'
  | 'league'
  | 'status'
  | 'health'
  | 'ttmRevenue'
  | 'activeDealCount'
  | 'ownerName';

interface Column {
  id: ColumnId;
  label: string;
  minWidth?: number;
  align?: 'center';
  sortable?: boolean;
  apiField?: string;
}

const columns: readonly Column[] = [
  { id: 'Name', label: 'Name', sortable: true, apiField: 'name' },
  { id: 'category', label: 'Type', minWidth: 100, sortable: false },
  { id: 'league', label: 'League', minWidth: 120, sortable: false },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    minWidth: 140,
    sortable: true,
    apiField: 'healthStatus',
  },
  { id: 'health', label: 'Health', minWidth: 220, sortable: true, apiField: 'healthScore' },
  {
    id: 'ttmRevenue',
    label: 'TTM Revenue',
    align: 'center',
    sortable: true,
    apiField: 'ttmRevenue',
  },
  {
    id: 'activeDealCount',
    label: 'Deals',
    align: 'center',
    sortable: true,
    apiField: 'activeDealCount',
  },
  { id: 'ownerName', label: 'Owner', minWidth: 120, sortable: true, apiField: 'ownerName' },
];

const ClientRowGrid = ({
  clients,
  category,
  loading,
  pagination,
  onSort,
}: {
  clients: ClientDataType;
  category: string | null;
  loading?: boolean;
  pagination?: PaginationProps;
  onSort?: (field: string, order: 'asc' | 'desc') => void;
}) => {
  const [sortAnchorEl, setSortAnchorEl] = useState<{ el: HTMLElement; id: ColumnId } | null>(null);
  const [sortOptions, setSortOptions] = useState<Partial<Record<ColumnId, 'asc' | 'desc'>>>({});

  const handleSortClick = (event: React.MouseEvent<HTMLElement>, id: ColumnId) => {
    setSortAnchorEl({ el: event.currentTarget, id });
  };

  const handleSortChange = (order: 'asc' | 'desc') => {
    if (!sortAnchorEl) return;
    const field = sortAnchorEl.id;
    const apiField = columns.find((c) => c.id === field)?.apiField || field;
    setSortOptions((prev) => ({ ...prev, [field]: order }));
    if (onSort) onSort(apiField, order);
    setSortAnchorEl(null);
  };

  const data =
    category === 'all'
      ? clients?.clients
      : clients?.clients.filter((val) => val.category === category);

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 250px)',
        }}
      >
        <CustomLoader open={true} />
      </Box>
    );

  const totalPages = pagination?.totalPages ?? 0;
  const handleFirstPage = () => {
    pagination?.setCurrentPage(0);
    pagination?.setPageWindowStart(0);
  };
  const handlePreviousPage = () => {
    if ((pagination?.currentPage ?? 0) > 0)
      pagination?.setCurrentPage((pagination?.currentPage ?? 0) - 1);
  };
  const handleNextPage = () => {
    if ((pagination?.currentPage ?? 0) < totalPages - 1)
      pagination?.setCurrentPage((pagination?.currentPage ?? 0) + 1);
  };
  const handleLastPage = () => {
    pagination?.setCurrentPage(totalPages - 1);
    pagination?.setPageWindowStart(Math.max(0, totalPages - 4));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none' }}>
        <TableContainer
          sx={{
            overflow: 'scroll',
            height: { xs: 'calc(100vh - 250px)', xl: 'calc(100vh - 310px)' },
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow
                sx={{
                  border: '1px solid black',
                  borderRadius: '14px',
                  '& th': {
                    backgroundColor: '#f5f5f5',
                  },
                  '& th:first-child': {
                    borderRadius: '14px 0px 0px 14px',
                  },
                  '& th:last-child': {
                    borderRadius: '0px 14px 14px 0px',
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.sortable ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Typography
                          variant='responsiveBody3'
                          sx={{ color: zeplinColor.BasicBlack }}
                        >
                          {column.label}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={(e) => handleSortClick(e, column.id)}
                          sx={{ padding: '2px' }}
                        >
                          <UnfoldMoreIcon
                            sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }}
                          />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                        {column.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, ind) => {
                return (
                  <TableRow
                    hover
                    role='checkbox'
                    tabIndex={-1}
                    key={ind}
                    sx={{
                      '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' },
                    }}
                  >
                    {columns.map((column) => {
                      const healthScore = getClientHealthScore(row);
                      const healthStatus = getClientStatusLabel(row);

                      return (
                        <TableCell key={column.id} align={column.align}>
                          <Link
                            to={`huddle/${row.AccountId}`}
                            state={{ client: row }}
                            style={{ textDecoration: 'none', color: zeplinColor.BasicBlack }}
                            onClick={() =>
                              sessionStorage.setItem('selectedClient', JSON.stringify(row))
                            }
                          >
                            {column.id === 'Name' ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  cursor: 'pointer',
                                }}
                              >
                                {row?.avatar ? (
                                  <img
                                    width={'60px'}
                                    height={'60px'}
                                    src={row?.avatar}
                                    alt='logo'
                                    style={{ objectFit: 'cover', borderRadius: '14px' }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: '60px',
                                      height: '60px',
                                      borderRadius: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor:
                                        row?.category === 'Talent'
                                          ? zeplinColor.Brand1
                                          : row?.category === 'Athlete'
                                            ? zeplinColor.USER_PROFILE_COLOR1
                                            : row?.category === 'Business'
                                              ? zeplinColor.USER_PROFILE_COLOR2
                                              : row?.category === 'Business_Development'
                                                ? zeplinColor.USER_PROFILE_COLOR3
                                                : '',
                                    }}
                                  >
                                    <Typography
                                      variant='display5'
                                      sx={{ color: zeplinColor.Neutral1 }}
                                    >
                                      {String(row.Name)
                                        .split(' ')
                                        .map((val, index, arr) =>
                                          index === 0 || index === arr.length - 1 ? val[0] : '',
                                        )}
                                    </Typography>
                                  </Box>
                                )}
                                {row.Name}
                              </Box>
                            ) : column.id === 'health' ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  minWidth: 180,
                                }}
                              >
                                <LinearProgress
                                  variant='determinate'
                                  value={healthScore}
                                  sx={{
                                    flex: 1,
                                    height: 8,
                                    borderRadius: '999px',
                                    backgroundColor: zeplinColor.Neutral4,
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: '999px',
                                      backgroundColor: getHealthBarColor(healthScore),
                                    },
                                  }}
                                />
                                <Typography variant='label1' color={getHealthBarColor(healthScore)}>
                                  {healthScore}%
                                </Typography>
                              </Box>
                            ) : column.id === 'status' ? (
                              <Chip
                                label={healthStatus}
                                sx={{
                                  backgroundColor: getStatusChipColor(healthStatus),
                                  color: zeplinColor.Neutral1,
                                  borderRadius: '50px',
                                  height: '24px',
                                  '& .MuiChip-label': { px: '10px' },
                                }}
                              />
                            ) : column.id === 'ttmRevenue' ? (
                              <Typography variant='body3' color={zeplinColor.BasicBlack}>
                                ${formatCurrency(Number(row.ttmRevenue || 0))}
                              </Typography>
                            ) : column.id === 'activeDealCount' ? (
                              <Typography variant='body3' color={zeplinColor.Brand1}>
                                {row.activeDealCount ?? 0}
                              </Typography>
                            ) : column.id === 'ownerName' ? (
                              <Typography variant='body3' color={zeplinColor.BasicBlack}>
                                {row.ownerName || 'N/A'}
                              </Typography>
                            ) : column.id === 'category' ? (
                              <Typography variant='body3' color={zeplinColor.BasicBlack}>
                                {row.category || '—'}
                              </Typography>
                            ) : column.id === 'league' ? (
                              <Typography variant='body3' color={zeplinColor.BasicBlack}>
                                {row.league || '—'}
                              </Typography>
                            ) : null}
                          </Link>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          count={pagination.totalCount}
          paginatedDataLength={data.length}
          onFirstPage={handleFirstPage}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onLastPage={handleLastPage}
          onPageChange={(page) => pagination.setCurrentPage(page)}
          onRowsPerPageChange={(val) => {
            pagination.setRowsPerPage(val);
            pagination.setCurrentPage(0);
          }}
          pageWindowStart={pagination.pageWindowStart}
          setPageWindowStart={pagination.setPageWindowStart}
          setCurrentPage={pagination.setCurrentPage}
          setRowsPerPage={pagination.setRowsPerPage}
        />
      )}

      <SortPopover
        anchorEl={sortAnchorEl?.el || null}
        setAnchorEl={() => setSortAnchorEl(null)}
        sortOption={sortAnchorEl ? sortOptions[sortAnchorEl.id] || 'asc' : 'asc'}
        onSortChange={handleSortChange}
        label={columns.find((c) => c.id === sortAnchorEl?.id)?.label || ''}
        ascLabel={
          sortAnchorEl?.id === 'ttmRevenue' ||
          sortAnchorEl?.id === 'activeDealCount' ||
          sortAnchorEl?.id === 'health'
            ? 'Lowest to Highest'
            : 'A to Z'
        }
        descLabel={
          sortAnchorEl?.id === 'ttmRevenue' ||
          sortAnchorEl?.id === 'activeDealCount' ||
          sortAnchorEl?.id === 'health'
            ? 'Highest to Lowest'
            : 'Z to A'
        }
      />
    </Box>
  );
};

export default ClientRowGrid;
